/**
 * LogCard — inline card the chat surface renders when the chat agent has
 * just landed a widget log row (S11/S12/S15 in task_plan.md).
 *
 * Single source of truth: the card's state is derived *from the server*.
 * On mount and after every button press, it GETs the data feed filtered by
 * its own `log_card_id`:
 *
 *   • row exists && !canceled_at → "Logged | Cancel"  (the happy path)
 *   • row exists &&  canceled_at → "Log    | Canceled" (user toggled back)
 *   • no row yet                 → "Log    | Canceled" (suggestable click
 *                                    that hasn't been confirmed yet)
 *
 * The two buttons are idempotent in their direction:
 *
 *   • Cancel → DELETE /api/widget-instances/{id}/data?log_card_id=…
 *   • Log    → POST   /api/widget-instances/{id}/data  (clears canceled_at
 *              on the existing row, or atomically instantiate+log if
 *              `instanceId` is null but `widgetId` is set — S15 click path).
 *
 * Duplicate presses while a request is in flight are no-ops (the button
 * disables itself).
 *
 * Props:
 *   logCardId    — required; idempotency key the server keys on.
 *   instanceId   — the widget_instance_id this log belongs to. If null
 *                  + widgetId set → S15 atomic instantiate-and-log path.
 *   widgetId     — used only for the S15 first-log instantiation flow.
 *   widgetTitle  — display name shown in the card header.
 *   kind         — payload "kind" (e.g. "meal").
 *   payload      — what to log (passed as-is to /data).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { widgetInstances, instantiateAndLog } from '../../services/widgetApi';
import { WidgetIcon } from './WidgetPanel';

const STYLE = {
  card: {
    border: '1px solid #E5E5EA',
    borderRadius: 12,
    padding: '12px 14px',
    background: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxWidth: 420,
  },
  header: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: '#1C1C1E' },
  title: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jumpBtn: {
    border: 'none', background: 'transparent', color: '#3478F6', cursor: 'pointer',
    fontSize: 16, padding: 0, lineHeight: 1,
  },
  payload: { fontSize: 12, color: '#3C3C43', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  actions: { display: 'flex', gap: 8 },
  btn: {
    flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E5EA',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    // Animate the Log→✓Logged flip (color/background) + the tap (transform) so
    // the card updates smoothly instead of a hard swap. Press-scale via the
    // injected `.logcard-btn:active` rule below.
    transition: 'opacity 0.15s, background-color 0.18s, border-color 0.18s, color 0.18s, transform 0.09s ease-out',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
  btnPrimary: { background: '#34C759', color: '#FFFFFF', borderColor: '#34C759' },
  btnLogged: { background: '#E5E7E5', color: '#34C759', borderColor: '#34C759' },
  btnCanceled: { background: '#FFF1F0', color: '#8E8E93', borderColor: '#E5E5EA' },
  btnNeutral: { background: 'transparent', color: '#1C1C1E' },
  spinner: {
    width: 12, height: 12, border: '2px solid #FFFFFF44', borderTopColor: '#FFFFFF',
    borderRadius: '50%', animation: 'logcard-spin 0.7s linear infinite',
  },
  // ── redesign: status badge (top-right) + one primary affordance ──────────────
  badge: {
    position: 'absolute', top: 12, right: 12, display: 'inline-flex', alignItems: 'center',
    gap: 5, height: 22, padding: '0 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
  },
  badgeLogged: { background: 'rgba(52,199,89,0.14)', color: '#0E7A32' },
  badgePending: { background: '#ECECF1', color: '#8E8E93' },
  badgeCanceled: { background: '#ECECF1', color: '#8E8E93' },
  badgeDeleted: { background: 'rgba(199,13,44,0.10)', color: '#C70D2C' },
  dot: { width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: 'logcard-breathe 1.3s ease-in-out infinite' },
  // The single primary button. `act` = the Log call-to-action (filled green);
  // `nav` = the calm "See in {name}" jump once the job's done.
  primary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
    height: 44, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer',
    transition: 'transform 0.08s ease, filter 0.15s ease',
  },
  primaryAct: { background: '#34C759', color: '#FFFFFF' },
  primaryNav: { background: '#ECECF1', color: '#1C1C1E' },
  arrow: { fontSize: 14, opacity: 0.8 },
  plus: { fontWeight: 800, fontSize: 17 },
  undo: { alignSelf: 'center', background: 'none', border: 'none', color: '#8E8E93', fontSize: 12.5, cursor: 'pointer', padding: '2px 8px' },
};

// Single source of truth: ALWAYS treat the server's view of the row as
// authoritative. Local state is a cache that the verb actions invalidate.
function deriveStatus(row) {
  if (!row) return 'absent';
  if (row.canceled_at) return 'canceled';
  return 'logged';
}

// The LAST server-resolved row per log_card_id, surviving component RE-MOUNTS.
// A new turn re-renders the session under a different key subtree (completed→live),
// so React unmounts+remounts this card and its `row` state resets to `undefined` —
// which would first-paint from the FROZEN `initialStatus` again. For a row the user
// deleted from the widget that means the stale "logged" card flashes back (the
// "deleted card resurrects on the next turn" bug). Seeding `row` from this cache on
// mount keeps the known-deleted (null) / known-logged state across the re-mount, so
// the frozen status never wins a second time. Keyed by the globally-unique log_card_id.
const _resolvedRowCache = new Map();

// Deterministic, VALID uuid from a string (FNV-1a over 4 salted passes → 32 hex
// in UUID shape). Used to derive a stable per-option log_card_id when the server
// didn't supply one — the DB column is UUID-typed, so the key must parse as a
// uuid. Mirrors WidgetSuggestionCard.stableUuid.
function stableUuid(str) {
  let hex = '';
  for (let salt = 0; salt < 4; salt++) {
    let h = (0x811c9dc5 ^ salt) >>> 0;
    const s = `${str}:${salt}`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    hex += (h >>> 0).toString(16).padStart(8, '0');
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

export default function LogCard({
  logCardId,
  // Server-stamped committed status ('logged' | 'canceled' | 'pending') from the
  // reconciled session view. Used for the FIRST paint so the card doesn't flash
  // the actionable "Log / Cancel" state before its own GET resolves (the jitter on
  // every session reopen / card jump). Once our refresh lands, the GET'd row wins.
  status: initialStatus = null,
  instanceId,
  widgetId,
  instantiateTitle = null,
  widgetTitle,
  kind,
  payload,
  sessionId = null,
  // Widget icon URL (release bundle) → shown as {icon} in the primary button and
  // the deleted-card kicker. Null → the button shows the name only (back-compat).
  widgetIcon = null,
  logOptions = null,
  // Per-field display hints from the widget's data_schema (server-stamped) →
  // the DESIGNED card template (hero title + stat grid + note). Null/empty →
  // the generic label/value box (PayloadInfo). Each entry: {key, role, label?,
  // unit?, highlight?}.
  display = null,
  // Base item's log_card_id when this card is a photo APPEND anchor (the data
  // view's "+"). Append cards are informational — no Logged ⇄ Cancel verbs
  // (the base row owns commitment); they exist as per-photo session anchors.
  appendsTo = null,
}) {
  const navigate = useNavigate();
  // `undefined` = our GET hasn't resolved yet → fall back to `initialStatus`;
  // after refresh it's a row object or `null` (absent), which is authoritative.
  // Seed from the cross-remount cache so a re-mounted card keeps its last-resolved
  // row (esp. a deleted `null`) instead of re-flashing the frozen `initialStatus`.
  const [row, setRow] = useState(() => (
    logCardId && _resolvedRowCache.has(logCardId) ? _resolvedRowCache.get(logCardId) : undefined
  ));
  const [busy, setBusy] = useState(false);
  // Which option index is mid-commit, so ONLY the tapped option shows a spinner
  // (others just disable). -1 = a non-option action (Log/Cancel/Recreate).
  const [busyOption, setBusyOption] = useState(-1);
  const [error, setError] = useState(null);
  // The instance this log lived in was deleted → its data feed 404s. We show a
  // graceful "Canceled — widget deleted" state instead of dumping the raw error.
  // Widget delete is SOFT server-side: the OWNER's instantiate paths auto-
  // reactivate the widget, so the card offers "Use this Widget" (instantiate →
  // open the dashboard) and "Log" (instantiate+log, reusing the original
  // log_card_id) when widget_id is known. A NON-owner gets HTTP 410
  // {code:"widget_deleted"} from those endpoints → `gone` hides the buttons.
  const [deleted, setDeleted] = useState(false);
  const [gone, setGone] = useState(false);
  const [resolvedInstanceId, setResolvedInstanceId] = useState(instanceId || null);
  const mounted = useRef(true);

  // Refresh: GET /data?log_card_id=… and stash the row (or null if absent).
  // This is the ONLY place that decides what the card shows.
  const refresh = useCallback(async (instanceIdOverride) => {
    const iid = instanceIdOverride || resolvedInstanceId;
    if (!iid || !logCardId) return;
    try {
      // An APPEND card (a photo-append anchor, or a convert/regenerate OUTPUT of a
      // multi-output document) has NO row of its own — its write merged into the
      // BASE row keyed by `appendsTo`. Resolving by the card's own `logCardId`
      // always comes back absent → the card falsely flips to "Deleted" seconds
      // after logging. Resolve committed state from the BASE row, like the server
      // reconciler does (`appends_to` set → committed by construction).
      const resolveId = appendsTo || logCardId;
      const data = await widgetInstances.data(iid, { log_card_id: resolveId });
      const entries = Array.isArray(data) ? data : (data?.entries || []);
      const resolved = entries[0] || null;
      // Remember the authoritative row so a later re-mount seeds from it (survives
      // the completed→live re-render) instead of the frozen initialStatus.
      _resolvedRowCache.set(logCardId, resolved);
      if (!mounted.current) return;
      setRow(resolved);
    } catch (e) {
      if (!mounted.current) return;
      // 404 = the instance was deleted; surface the deleted state, not the raw error.
      if (e?.status === 404) setDeleted(true);
      else setError(e?.message || String(e));
    }
  }, [resolvedInstanceId, logCardId, appendsTo]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  // Shared wrapper for the two verb actions. Guards `busy`, clears error,
  // applies the request's result row (if any) via `applyResult`, and surfaces
  // failures — all the wiring that would otherwise repeat in onLog / onCancel.
  const runAction = useCallback(async (fn, applyResult, optionIndex = -1) => {
    if (busy) return;
    setBusy(true);
    setBusyOption(optionIndex);
    setError(null);
    try {
      const result = await fn();
      if (!mounted.current) return;
      applyResult?.(result);
      // A verb landed (log/cancel committed server-side) → tell the playground
      // to refresh the in-session widget panel so its Logged section flips
      // pending → ✓ immediately. Same window-event contract as vi-card-reply.
      if (sessionId) {
        window.dispatchEvent(new CustomEvent('vi-widget-log-changed', { detail: { sessionId } }));
      }
    } catch (e) {
      if (!mounted.current) return;
      // The instance vanished between render and this action → deleted state.
      if (e?.status === 404) setDeleted(true);
      // 410 widget_deleted = the parent widget is soft-deleted and we're NOT
      // the owner (owner paths auto-reactivate) → plain canceled, no buttons.
      else if (e?.status === 410) { setDeleted(true); setGone(true); }
      else setError(e?.message || String(e));
    } finally {
      if (mounted.current) { setBusy(false); setBusyOption(-1); }
    }
  }, [busy, sessionId]);

  // Both verbs re-derive state from the authoritative server GET (`refresh`)
  // rather than trusting the POST/DELETE response shape — the verb endpoints
  // return `{entry: row}` while the GET returns `{entries: [...]}`, and
  // `deriveStatus` reads the bare row. Refreshing keeps the single-source-of-
  // truth contract and avoids the wrapper-shape mismatch.
  const onLog = useCallback(() => runAction(async () => {
    if (resolvedInstanceId) {
      await widgetInstances.appendData(resolvedInstanceId, { kind, payload, log_card_id: logCardId, source_session_id: sessionId });
      return { instanceId: resolvedInstanceId };
    }
    if (widgetId) {
      // S15 path — first log against a widget the user hasn't instantiated yet.
      const result = await instantiateAndLog({ widget_id: widgetId, log_card_id: logCardId, kind, payload, source_session_id: sessionId });
      return { instanceId: result?.instance?.instance_id || null };
    }
    throw new Error('LogCard needs either instanceId or widgetId.');
  }, async (result) => {
    const iid = result?.instanceId || null;
    if (iid && iid !== resolvedInstanceId) setResolvedInstanceId(iid);
    await refresh(iid);
  }), [runAction, refresh, resolvedInstanceId, widgetId, kind, payload, logCardId, sessionId]);

  const onCancel = useCallback(() => {
    if (!resolvedInstanceId) return;
    runAction(
      () => widgetInstances.cancelData(resolvedInstanceId, logCardId),
      () => refresh(resolvedInstanceId),
    );
  }, [runAction, refresh, resolvedInstanceId, logCardId]);

  // ── log_options ──────────────────────────────────────────────────────────
  // Each option is an INDEPENDENT row keyed by `${logCardId}::<i>`, so a
  // multiple_choice card can log several options as separate widget_user_data
  // rows. radio = mutually exclusive (one logged → others disabled + a Cancel
  // below). The chosen option's `prompt` is baked into the logged payload
  // (`log_option`) so the widget can read which group it landed in; an optional
  // `set` patch on the option is merged into the row to ROUTE it (set the
  // widget's real grouping field, e.g. `{ownership:"wild"}`).
  const optionList = Array.isArray(logOptions?.options) ? logOptions.options : null;
  const optionType = logOptions?.type === 'multiple_choice' ? 'multiple_choice' : 'radio';
  // Seed each option's logged state from the server-stamped `opt.logged` (set by
  // the reconciler) so a log_options card paints its ✓ options on first render
  // instead of flashing them un-logged before refreshOption resolves.
  const [optionRows, setOptionRows] = useState(() => {
    const seed = {};
    if (Array.isArray(logOptions?.options)) {
      logOptions.options.forEach((opt, i) => {
        if (opt && opt.logged) seed[i] = { canceled_at: null, _seedFromServer: true };
      });
    }
    return seed;
  });
  // Per-option idempotency key. Each option commits an independent row and the
  // DB `log_card_id` is a UUID column, so the key MUST be a valid UUID — prefer
  // the server-assigned id (a uuid5), else derive a deterministic VALID uuid
  // from the base + index. (The old `${logCardId}::${i}` form is NOT a uuid and
  // 400s; any card whose log_options lacks server ids hit that.)
  const optLcid = useCallback(
    (i) => optionList?.[i]?.log_card_id || stableUuid(`${logCardId}:opt:${i}`),
    [logCardId, optionList],
  );

  const refreshOption = useCallback(async (i, iidOverride) => {
    const iid = iidOverride || resolvedInstanceId;
    if (!iid) { setOptionRows((p) => ({ ...p, [i]: null })); return; }
    try {
      const data = await widgetInstances.data(iid, { log_card_id: optLcid(i) });
      const entries = Array.isArray(data) ? data : (data?.entries || []);
      if (!mounted.current) return;
      setOptionRows((p) => ({ ...p, [i]: entries[0] || null }));
    } catch (e) {
      if (!mounted.current) return;
      if (e?.status === 404) setDeleted(true);
    }
  }, [resolvedInstanceId, optLcid]);

  // Initial + on-instance-change refresh of every option's server state.
  useEffect(() => {
    if (!optionList || !resolvedInstanceId) return;
    optionList.forEach((_, i) => refreshOption(i, resolvedInstanceId));
  }, [optionList, resolvedInstanceId, refreshOption]);

  const logOption = useCallback((i, opt) => runAction(async () => {
    const lc = optLcid(i);
    // Merge order: base payload → option's `set` patch (routes the row into a
    // group by overriding the widget's real field, e.g. ownership) → the
    // log_option bookkeeping. `set` wins over the agent's base guess.
    const optSet = (opt.set && typeof opt.set === 'object' && !Array.isArray(opt.set)) ? opt.set : {};
    const optPayload = { ...(payload || {}), ...optSet, log_option: opt.prompt, log_option_label: opt.label };
    if (resolvedInstanceId) {
      await widgetInstances.appendData(resolvedInstanceId, { kind, payload: optPayload, log_card_id: lc, source_session_id: sessionId });
      return { instanceId: resolvedInstanceId, i };
    }
    if (widgetId) {
      const result = await instantiateAndLog({ widget_id: widgetId, log_card_id: lc, kind, payload: optPayload, source_session_id: sessionId });
      return { instanceId: result?.instance?.instance_id || null, i };
    }
    throw new Error('LogCard needs either instanceId or widgetId.');
  }, async (result) => {
    const iid = result?.instanceId || null;
    if (iid && iid !== resolvedInstanceId) setResolvedInstanceId(iid);
    await refreshOption(result.i, iid);
  }, i), [runAction, refreshOption, resolvedInstanceId, widgetId, kind, payload, sessionId, optLcid]);

  const cancelOption = useCallback((i) => {
    if (!resolvedInstanceId) return;
    runAction(
      () => widgetInstances.cancelData(resolvedInstanceId, optLcid(i)),
      () => refreshOption(i, resolvedInstanceId),
      i,
    );
  }, [runAction, refreshOption, resolvedInstanceId, optLcid]);

  const isOptionLogged = (i) => { const r = optionRows[i]; return !!(r && !r.canceled_at); };
  const anyOptionLogged = optionList ? optionList.some((_, i) => isOptionLogged(i)) : false;

  const onJump = useCallback(() => {
    if (!resolvedInstanceId) return;
    navigate(`/playground/widget/studio/instance/${resolvedInstanceId}`);
  }, [navigate, resolvedInstanceId]);

  // "Log" on a deleted-widget card — the combined instantiate+log endpoint.
  // The owner path auto-reactivates the soft-deleted widget; we keep reusing
  // the card's ORIGINAL log_card_id so the row stays idempotent. Adopts the
  // minted instance so ↗/Log/Cancel work again. Non-owner → 410 (runAction
  // flips `gone`).
  const onRecreate = useCallback(() => {
    if (!widgetId) return;
    runAction(
      () => instantiateAndLog({
        widget_id: widgetId,
        log_card_id: logCardId,
        kind,
        payload,
        title: instantiateTitle || widgetTitle || null,
        source_session_id: sessionId,
      }),
      (result) => {
        if (result?.schema_warnings) console.debug('[LogCard] schema_warnings:', result.schema_warnings);
        const iid = result?.instance?.instance_id || null;
        if (iid) {
          setResolvedInstanceId(iid);
          setDeleted(false);
          refresh(iid);
        }
      },
    );
  }, [runAction, refresh, widgetId, logCardId, kind, payload, instantiateTitle, widgetTitle, sessionId]);

  // "Use this Widget" on a deleted-widget card — instantiate only (the owner
  // path auto-reactivates the soft-deleted widget) and jump straight to the
  // instance dashboard. Non-owner → 410 (runAction flips `gone`).
  const onUseWidget = useCallback(() => {
    if (!widgetId) return;
    runAction(
      () => widgetInstances.instantiate(widgetId, {
        title: instantiateTitle || widgetTitle || undefined,
      }),
      (result) => {
        if (result?.schema_warnings) console.debug('[LogCard] schema_warnings:', result.schema_warnings);
        const iid = result?.instance_id || result?.instance?.instance_id || null;
        if (iid) navigate(`/playground/widget/studio/instance/${iid}`);
      },
    );
  }, [runAction, widgetId, instantiateTitle, widgetTitle, navigate]);

  // Before our GET resolves (`row === undefined`), trust the server-stamped status
  // so the first paint is correct; afterwards the GET'd row is the source of truth.
  const status = row === undefined
    ? (initialStatus === 'logged' ? 'logged' : initialStatus === 'canceled' ? 'canceled' : 'absent')
    : deriveStatus(row);
  const summary = summarizePayload(payload);
  // Thumbnail from the AUTHORITATIVE fetched row, not the frozen card payload: a
  // piece logs image_url = the source-photo PLACEHOLDER + a media_slot, and the
  // host swaps in the generated cutout on the widget_user_data ROW (the gallery),
  // never back into the session card. `row` (our own GET) carries that backfilled
  // image, so the thumb shows the real cutout; fall back to the prop pre-fetch.
  const thumb = payloadThumb((row && typeof row === 'object' && row.payload) || payload);

  // Deleted state — the parent widget was (soft-)deleted, so this log's
  // instance feed is gone. Renders CANCELED-style. Owner affordances:
  //   • "Use this Widget" → instantiate (auto-reactivates) → open the dashboard
  //   • "Log"             → instantiate+log with the original log_card_id
  // `gone` (HTTP 410 widget_deleted = non-owner) drops both buttons.
  if (deleted) {
    return (
      <div id={logCardId ? `log-card-${logCardId}` : undefined} data-log-card-id={logCardId || undefined} style={{ ...STYLE.card, position: 'relative', background: '#FAFAFA', borderStyle: 'dashed', scrollMarginTop: 80 }}>
        <style>{`@keyframes logcard-spin { to { transform: rotate(360deg); } } @keyframes logcard-breathe { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } } [data-log-card-id] button:active:not(:disabled) { transform: scale(0.97); }`}</style>
        <span style={{ ...STYLE.badge, ...STYLE.badgeDeleted }}>Deleted</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 84 }}>
          {widgetIcon && <WidgetIcon url={widgetIcon} size={18} radius={5} />}
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1C1C1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widgetTitle || 'Log'}</span>
        </div>
        {summary && <div style={{ ...STYLE.payload, color: '#8E8E93' }}>{summary}</div>}
        <div style={{ fontSize: 12, color: '#8E8E93' }}>This widget was removed. Re-adopt it to keep logging here.</div>
        {widgetId && !gone && (
          <div style={STYLE.actions}>
            <button onClick={onUseWidget} disabled={busy} style={{ ...STYLE.primary, flex: 1, background: '#1C1C1E', color: '#FFFFFF', opacity: busy ? 0.6 : 1 }}>
              {busy ? <span style={STYLE.spinner} /> : 'Use this widget'}
            </button>
            <button onClick={onRecreate} disabled={busy} style={{ ...STYLE.primary, ...STYLE.primaryNav, flex: 1, opacity: busy ? 0.6 : 1 }}>
              {busy ? <span style={{ ...STYLE.spinner, borderTopColor: '#1C1C1E' }} /> : 'Log again'}
            </button>
          </div>
        )}
        {error && <div style={{ fontSize: 11, color: '#FF3B30' }}>{error}</div>}
      </div>
    );
  }

  // The playground is developer-facing — a log card whose entry the user DELETED
  // from the widget must NOT vanish and must never flash back as "Logged" on the
  // next turn (the resurrection bug). Once our GET resolves the row as ABSENT
  // (`null`) for a card that WAS committed (server-stamped logged/canceled with a
  // real log_card_id), render it plainly MARKED "Deleted": visible, inert, no
  // re-log affordance. The badge alone carries the deleted state — the text and
  // thumbnail render exactly like a normal logged card (no strikethrough, no
  // dimming, no desaturation). `_resolvedRowCache` seeds `row` = null across
  // re-mounts, so this stays stable instead of re-flashing the frozen status. The
  // app (user-facing) HIDES these instead; this branch is web-only by construction.
  const entryRemoved = row === null && logCardId
    && (initialStatus === 'logged' || initialStatus === 'canceled');
  if (entryRemoved) {
    return (
      <div id={`log-card-${logCardId}`} data-log-card-id={logCardId} style={{ ...STYLE.card, position: 'relative', scrollMarginTop: 80 }}>
        <span style={{ ...STYLE.badge, ...STYLE.badgeDeleted }}>Deleted</span>
        <div style={{ paddingRight: 84, display: 'flex', gap: 10 }}>
          {thumb && (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{ width: 56, height: 56, flexShrink: 0, objectFit: 'cover', borderRadius: 8, background: '#F0F0F2' }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {Array.isArray(display) && display.length > 0 ? (
              <DesignedPayload display={display} payload={payload} fallback={summary} />
            ) : (
              <PayloadInfo payload={payload} fallback={summary} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── redesign: one primary affordance whose verb follows the log state ────────
  // Status → top-right badge; the loud button is "Log to {icon}{name}" when idle
  // (green CTA) and "See in {icon}{name}" once logged (calm jump). Cancel is a
  // quiet Undo. Mirrors the panel R: idle→LOGS, logged→JUMPS.
  const icon = widgetIcon ? <WidgetIcon url={widgetIcon} size={18} radius={5} /> : null;
  const name = widgetTitle || 'widget';
  const optionLoggedCount = optionList ? optionList.reduce((n, _, i) => n + (isOptionLogged(i) ? 1 : 0), 0) : 0;
  const showSeeIn = status === 'logged';
  const seeInBtn = (
    <button
      onClick={onJump}
      disabled={!resolvedInstanceId}
      title="Open widget"
      aria-label={`See in ${name}`}
      style={{ ...STYLE.primary, ...STYLE.primaryNav, opacity: resolvedInstanceId ? 1 : 0.55, cursor: resolvedInstanceId ? 'pointer' : 'default' }}
    >
      {icon}<span>See in {name}</span><span style={STYLE.arrow}>↗</span>
    </button>
  );
  const logToBtn = (
    <button onClick={onLog} disabled={busy} style={{ ...STYLE.primary, ...STYLE.primaryAct, opacity: busy ? 0.7 : 1 }}>
      {busy ? <span style={STYLE.spinner} /> : (<>{icon}<span>Log to {name}</span><span style={STYLE.plus}>＋</span></>)}
    </button>
  );
  const badgeEl = optionList
    ? (optionLoggedCount > 0
      ? <span style={{ ...STYLE.badge, ...STYLE.badgeLogged }}>✓ {optionType === 'multiple_choice' && optionLoggedCount > 1 ? `${optionLoggedCount} logged` : 'Logged'}</span>
      : null)
    : status === 'logged'
      ? <span style={{ ...STYLE.badge, ...STYLE.badgeLogged }}>✓ Logged</span>
      : status === 'canceled'
        ? <span style={{ ...STYLE.badge, ...STYLE.badgeCanceled }}>Canceled</span>
        : null;

  return (
    <div id={logCardId ? `log-card-${logCardId}` : undefined} data-log-card-id={logCardId || undefined} style={{ ...STYLE.card, position: 'relative', scrollMarginTop: 80 }}>
      <style>{`@keyframes logcard-spin { to { transform: rotate(360deg); } } @keyframes logcard-breathe { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } } [data-log-card-id] button:active:not(:disabled) { transform: scale(0.97); }`}</style>
      {badgeEl}
      <div style={{ paddingRight: badgeEl ? 84 : 0, display: 'flex', gap: 10 }}>
        {thumb && (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{ width: 56, height: 56, flexShrink: 0, objectFit: 'cover', borderRadius: 8, background: '#F0F0F2' }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {Array.isArray(display) && display.length > 0 ? (
            <DesignedPayload display={display} payload={payload} fallback={summary} />
          ) : (
            <PayloadInfo payload={payload} fallback={summary} />
          )}
        </div>
      </div>
      {appendsTo ? (
        // Photo-append anchor: informational (the base row owns commitment) — no
        // Log verb, but keep the "See in {name}" jump when the instance is known.
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#8E8E93' }}>
            <span style={{ color: '#34C759', fontSize: 14, lineHeight: 1 }}>＋</span>
            Photo added to {widgetTitle || 'the item'}
          </div>
          {resolvedInstanceId && seeInBtn}
        </>
      ) : optionList ? (
        // log_options: each option is the log action. Once ≥1 is logged the
        // "See in {name}" primary appears; radio adds a quiet "Undo choice".
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {optionList.map((opt, i) => {
            const logged = isOptionLogged(i);
            if (optionType === 'radio') {
              const blocked = anyOptionLogged && !logged;
              return (
                <button
                  key={i}
                  onClick={() => logOption(i, opt)}
                  disabled={busy || logged || blocked}
                  style={{
                    ...STYLE.btn, ...(logged ? STYLE.btnLogged : STYLE.btnNeutral),
                    justifyContent: 'flex-start',
                    opacity: blocked ? 0.45 : busy && !logged && busyOption !== i ? 0.6 : 1,
                    cursor: logged || blocked ? 'default' : 'pointer',
                  }}
                >{busyOption === i ? <span style={{ ...STYLE.spinner, borderTopColor: '#1C1C1E' }} /> : (logged ? `✓ ${opt.label}` : opt.label)}</button>
              );
            }
            // multiple_choice: each option locks independently; quiet Undo to its right.
            return (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button
                  onClick={() => logOption(i, opt)}
                  disabled={busy || logged}
                  style={{
                    ...STYLE.btn, ...(logged ? STYLE.btnLogged : STYLE.btnNeutral),
                    justifyContent: 'flex-start', flex: 1,
                    opacity: busy && !logged && busyOption !== i ? 0.6 : 1, cursor: logged ? 'default' : 'pointer',
                  }}
                >{busyOption === i ? <span style={{ ...STYLE.spinner, borderTopColor: '#1C1C1E' }} /> : (logged ? `✓ ${opt.label}` : opt.label)}</button>
                {logged && (
                  <button onClick={() => cancelOption(i)} disabled={busy} style={STYLE.undo}>
                    {busyOption === i ? 'Undoing…' : 'Undo'}
                  </button>
                )}
              </div>
            );
          })}
          {anyOptionLogged && seeInBtn}
          {optionType === 'radio' && anyOptionLogged && (
            <button
              onClick={() => { const i = optionList.findIndex((_, j) => isOptionLogged(j)); if (i >= 0) cancelOption(i); }}
              disabled={busy}
              style={STYLE.undo}
            >Undo choice</button>
          )}
          {!anyOptionLogged && (
            // Nothing picked yet → let the user DISMISS the pending options card
            // (writes a canceled marker for the base id → clears the "Pending —
            // tap to view" placeholder that otherwise persists un-resolvably).
            <button onClick={onCancel} disabled={busy} style={STYLE.undo}>
              {busy ? 'Dismissing…' : 'Dismiss'}
            </button>
          )}
        </div>
      ) : showSeeIn ? (
        <>
          {seeInBtn}
          <button onClick={onCancel} disabled={busy} style={STYLE.undo}>
            {busy ? 'Undoing…' : 'Undo'}
          </button>
        </>
      ) : (
        <>
          {logToBtn}
          {status !== 'canceled' && (
            <button onClick={onCancel} disabled={busy} style={STYLE.undo}>
              {busy ? 'Dismissing…' : 'Dismiss'}
            </button>
          )}
        </>
      )}
      {error && <div style={{ fontSize: 11, color: '#FF3B30' }}>{error}</div>}
    </div>
  );
}

// ── structured info card (F4) ────────────────────────────────────────────────
// Payload keys that never belong on the glance card: media internals, the
// commit-routing marker, and ids. Mirrors widget_log_card.dart + data_view.
const HIDDEN_PAYLOAD_KEYS = new Set([
  'images', 'image_url', 'image_cards', 'image_cards_by_index', 'media_url',
  'photo_url', 'photo', 'log_option', 'log_option_label', 'summary', 'id',
  'kind', 'source_session_id', 'session_id',
  // Machine handles — a media ref / storage URL is NEVER display text (the widget
  // rule: show a name / format / Open, never the ref itself). Without this the
  // doc-digitizer output logged its giant signed GCS `output_url` as a raw
  // "Output Url: https://…?X-Goog-Signature=…" row (staging 90fda8f1).
  'output_url', 'output', 'media_ref', 'file_url', 'document_url', 'download_url',
  'url', 'uri', 'src', 'href', 'outputs',
]);

// A key that carries a machine handle (a url / ref / media pointer / mime), never
// display text — hidden generically so a new `*_url`/`*_ref` field the model grows
// next can't leak a raw handle into the card (Contract rule 4, the KV_HIDDEN rule).
const _HANDLE_KEY_RE = /(_url|_ref|_uri|_href|_src|_media|mime_type)$/i;
function isHiddenPayloadKey(k) {
  return HIDDEN_PAYLOAD_KEYS.has(k) || _HANDLE_KEY_RE.test(String(k));
}
const PAYLOAD_TITLE_KEYS = ['name', 'meal_name', 'title', 'common_name', 'model', 'species', 'label'];

// A glance thumbnail of what was LOGGED (e.g. a garment cutout), so the user can
// SEE the piece, not just its name. Read the item's CANONICAL image only:
// `image_url` (the piece's display image — a generated cutout is backfilled here
// via `media_slot`), or `media_url` (which the same backfill also sets). NEVER
// `photo_url`/`images` — those are the ORIGINAL source photo (provenance), and
// for a piece whose cutout is still generating that source is the input selfie.
// Showing it was the "all thumbnails are the input photo" bug: the agent logs the
// piece with only `media_slot` (image_url absent until the cutout backfills), so a
// pending piece shows no thumb rather than the wrong selfie.
function payloadThumb(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const cand = payload.image_url || payload.media_url;
  const s = typeof cand === 'string' ? cand.trim() : '';
  return s || null;
}

function payloadTitleKey(payload) {
  for (const k of PAYLOAD_TITLE_KEYS) {
    if (typeof payload?.[k] === 'string' && payload[k].trim()) return k;
  }
  return null;
}

function prettyPayloadKey(k) {
  return String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Stringify a payload value for display (array → joined). Empty for null/blank.
function fmtVal(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.filter((x) => x != null && String(x).trim()).join(', ');
  if (typeof v === 'object') return '';
  return String(v).trim();
}

/** Designed card body driven by the widget's data_schema display hints — a hero
 *  title, meta subtitle line(s), a stat grid (one accent-filled tile), and muted
 *  note line(s). Renders the same shape the agent used to freestyle in HTML, but
 *  from the REAL logged payload. Entries with a missing value are skipped; if the
 *  whole spec resolves to nothing, falls back to the generic info box. */
function DesignedPayload({ display, payload, fallback }) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const pick = (role) => display.filter((d) => d && d.role === role && fmtVal(p[d.key]));
  const titleEntry = pick('title')[0] || null;
  const subtitles = pick('subtitle');
  const stats = pick('stat');
  const notes = pick('note');
  if (!titleEntry && !subtitles.length && !stats.length && !notes.length) {
    return <PayloadInfo payload={payload} fallback={fallback} />;
  }
  // Honor explicit highlight; else the first stat is the accent tile (matches
  // the freeform card highlighting KCAL).
  const hasExplicitHl = stats.some((s) => s.highlight);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {titleEntry && (
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25, color: '#1C1C1E' }}>
          {fmtVal(p[titleEntry.key])}
        </div>
      )}
      {subtitles.map((s) => (
        <div key={s.key} style={{ fontSize: 12.5, lineHeight: 1.5, color: '#3C3C43' }}>{fmtVal(p[s.key])}</div>
      ))}
      {stats.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
          {stats.map((s, i) => {
            const hl = hasExplicitHl ? s.highlight : i === 0;
            return (
              <div
                key={s.key}
                style={{
                  flex: 1, minWidth: 64, textAlign: 'center', borderRadius: 10, padding: '8px 12px',
                  background: hl ? '#111111' : '#F0F0F2',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, color: hl ? '#FFFFFF' : '#1C1C1E' }}>
                  {fmtVal(p[s.key])}{s.unit || ''}
                </div>
                <div style={{ fontSize: 10, letterSpacing: 0.5, marginTop: 1, color: hl ? 'rgba(255,255,255,0.75)' : '#8A8A8E' }}>
                  {(s.label || prettyPayloadKey(s.key)).toUpperCase()}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {notes.map((n) => (
        <div key={n.key} style={{ fontSize: 11, color: '#8A8A8E', marginTop: 2 }}>{fmtVal(p[n.key])}</div>
      ))}
    </div>
  );
}

/** The card's info section — item title + label/value rows (a little info
 *  card, like the data view's Info block) instead of a flat text dump. Falls
 *  back to the legacy one-line summary when nothing structured exists. */
function PayloadInfo({ payload, fallback }) {
  if (!payload || typeof payload !== 'object') {
    return fallback ? <div style={STYLE.payload}>{fallback}</div> : null;
  }
  const titleKey = payloadTitleKey(payload);
  const rows = [];
  for (const [k, v] of Object.entries(payload)) {
    if (rows.length >= 6) break;
    if (isHiddenPayloadKey(k) || k === titleKey) continue;
    if (v == null || (typeof v === 'object' && !Array.isArray(v))) continue;
    const s = Array.isArray(v) ? v.join(', ') : String(v);
    if (!s.trim()) continue;
    rows.push([prettyPayloadKey(k), s.trim()]);
  }
  if (!titleKey && !rows.length) {
    return fallback ? <div style={STYLE.payload}>{fallback}</div> : null;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {titleKey && (
        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25, color: '#1C1C1E' }}>
          {payload[titleKey].trim()}
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ border: '0.7px solid #E5E5EA', borderRadius: 10, background: '#F7F7FA', overflow: 'hidden' }}>
          {rows.map(([k, v], i) => (
            <div
              key={k}
              style={{
                display: 'flex', gap: 8, padding: '6px 10px', fontSize: 11.5,
                borderTop: i === 0 ? 'none' : '0.5px solid #E5E5EA',
              }}
            >
              <div style={{ width: 96, flexShrink: 0, color: '#8E8E93', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k}</div>
              <div style={{ flex: 1, color: '#1C1C1E', wordBreak: 'break-word' }}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// One-line glance summary of the payload — best-effort, never throws.
function summarizePayload(payload) {
  if (!payload || typeof payload !== 'object') return '';
  if (typeof payload.summary === 'string') return payload.summary;
  if (typeof payload.title === 'string' && typeof payload.detail === 'string') {
    return `${payload.title} — ${payload.detail}`;
  }
  if (typeof payload.name === 'string' && payload.kcal != null) {
    return `${payload.name} · ${payload.kcal} kcal`;
  }
  try {
    const keys = Object.keys(payload).slice(0, 3);
    return keys.map((k) => `${k}: ${JSON.stringify(payload[k])}`).join(' · ');
  } catch { return ''; }
}
